import { AxiosResponse } from 'axios';
import _ from 'lodash';

import { PackMediaSection } from './PackMediaSection';
import { PackScreenshotsSection } from './PackScreenshotsSection';
import { PackVideosSection } from './PackVideosSection';

import { ImageFileUploading, VideoFileUploading } from '../../utils/upload';

import { MediaType } from '../../types/enums/mediatype.enum';
import { PackType } from '../../types/enums/packtype.enum';

import { Media } from '../../types/mediaTypes';
import { Pack } from '../../types/packTypes';

type MediaOrImageFileUploading = Media | (ImageFileUploading & { id?: number });
type MediaOrVideoFileUploading = Media | (VideoFileUploading & { id?: number });

const mediaListForType = (
    media: PackScreenshotsProps['media'],
    type: MediaType,
): (MediaOrImageFileUploading | MediaOrVideoFileUploading)[] => {
    switch (type) {
        case MediaType.SCREENSHOT:
            return media.screenshots;
        case MediaType.VIDEO:
            return media.videos;
        case MediaType.THUMBNAIL:
            return media.thumbnails;
        case MediaType.WIDE_THUMBNAIL:
            return media.widethumbnails;
        case MediaType.TAB_THUMBNAIL:
            return media.tabthumbnails;
        case MediaType.IAP_IMAGE:
            return media.iapimages;
        default:
            return [];
    }
};

type PackScreenshotsProps = {
    pack: Pack;
    media: {
        screenshots: MediaOrImageFileUploading[];
        videos: MediaOrVideoFileUploading[];
        thumbnails: MediaOrImageFileUploading[];
        widethumbnails: MediaOrImageFileUploading[];
        tabthumbnails: MediaOrImageFileUploading[];
        iapimages: MediaOrImageFileUploading[];
    };
    childPacksData: Pack[];
    onMediaChanged: (
        type: MediaType,
        updater: (
            prevItems: (
                | MediaOrImageFileUploading
                | MediaOrVideoFileUploading
            )[],
        ) => (MediaOrImageFileUploading | MediaOrVideoFileUploading)[],
    ) => void;
    toggleScreenshot: (mediaId: number) => void;
    onMediaProcessingFailed?: (mediaId: number, message: string) => void;
    onFailedMediaRemoved?: (mediaId: number) => void;
};

export const PackScreenshotsWrapper = (props: PackScreenshotsProps) => {
    const {
        pack,
        media,
        childPacksData,
        toggleScreenshot,
        onMediaChanged,
        onMediaProcessingFailed,
        onFailedMediaRemoved,
    } = props;

    const {
        screenshots,
        videos,
        thumbnails,
        widethumbnails,
        tabthumbnails,
        iapimages,
    } = media;

    const replaceMediaWhere = (
        type: MediaType,
        match: (
            m: MediaOrImageFileUploading | MediaOrVideoFileUploading,
        ) => boolean,
        replace: (
            m: MediaOrImageFileUploading | MediaOrVideoFileUploading,
        ) => MediaOrImageFileUploading | MediaOrVideoFileUploading,
    ) => {
        onMediaChanged(type, (prev) =>
            prev.map((m) => (match(m) ? replace(m) : m)),
        );
    };

    const matchByFileName = (fileName: string) =>
        function match(
            m: MediaOrImageFileUploading | MediaOrVideoFileUploading,
        ) {
            return 'file' in m && m.file.name === fileName;
        };

    const onPhotosSelected = (type: MediaType, files: ImageFileUploading[]) => {
        onMediaChanged(type, (prev) => _.concat(prev, files));
    };

    const onPhotosWasUploaded = (
        type: MediaType,
        file: File,
        res: AxiosResponse<{ uploaded: Media }>,
    ) => {
        replaceMediaWhere(type, matchByFileName(file.name), (m) =>
            _.assign(m, {
                uploading: false,
                id: res.data.uploaded.id,
                processingStatus: res.data.uploaded.processingStatus,
                processingProgress: res.data.uploaded.processingProgress,
            }),
        );
    };

    const onPhotoProcessingComplete = (type: MediaType) => (media: Media) => {
        replaceMediaWhere(
            type,
            (m) => m.id === media.id,
            () => media,
        );
    };

    const onPhotoProcessingError =
        (type: MediaType) => (mediaId: number, message: string) => {
            onMediaProcessingFailed?.(mediaId, message);
            replaceMediaWhere(
                type,
                (m) =>
                    m.id === mediaId &&
                    (!('processingStatus' in m) ||
                        m.processingStatus !== 'error'),
                (m) => _.assign({}, m, { processingStatus: 'error' as const }),
            );
        };

    const imageProcessingHandlers = (type: MediaType) => ({
        onProcessingComplete: onPhotoProcessingComplete(type),
        onProcessingError: (mediaId: number) =>
            onPhotoProcessingError(type)(
                mediaId,
                'An image could not be encoded on the server. Remove it and upload it again as a PNG.',
            ),
    });

    const onVideoSelected = (file: VideoFileUploading) => {
        onMediaChanged(MediaType.VIDEO, (prev) => _.concat(prev, file));
    };

    const onVideoWasUploaded = (file: File, media: Media) => {
        replaceMediaWhere(MediaType.VIDEO, matchByFileName(file.name), (m) =>
            _.assign(m, {
                uploading: false,
                url: media.url,
                id: media.id,
            }),
        );
    };

    const onMediaDelete = (index: number, type: MediaType) => {
        // Deleting a media whose server-side processing failed also clears its
        // error message from the section attention marker.
        const deleted = mediaListForType(media, type)[index];
        if (
            deleted &&
            'processingStatus' in deleted &&
            deleted.processingStatus === 'error' &&
            deleted.id != null
        ) {
            onFailedMediaRemoved?.(deleted.id);
        }
        onMediaChanged(type, (prev) => prev.filter((_item, i) => i !== index));
    };

    const onMediaDragEnd = (result: {
        source: { index: number; droppableId: MediaType };
        destination: { index: number };
    }) => {
        if (!result.destination) return;
        const destinationIndex = result.destination.index;

        onMediaChanged(result.source.droppableId, (prev) => {
            const mediaList = Array.from(prev);
            const [removed] = mediaList.splice(result.source.index, 1);
            mediaList.splice(destinationIndex, 0, removed);
            return mediaList;
        });
    };

    return (
        <>
            <PackScreenshotsSection
                pack={pack}
                childPacksData={childPacksData}
                items={screenshots}
                onMediaDelete={onMediaDelete}
                onMediasSelected={onPhotosSelected}
                onMediasWasUploaded={onPhotosWasUploaded}
                onMediaDragEnd={onMediaDragEnd}
                toggleScreenshot={toggleScreenshot}
                onScreenshotProcessingComplete={onPhotoProcessingComplete(
                    MediaType.SCREENSHOT,
                )}
                onScreenshotProcessingError={onPhotoProcessingError(
                    MediaType.SCREENSHOT,
                )}
            />
            <PackVideosSection
                items={videos}
                onVideoDelete={onMediaDelete}
                onVideoSelected={onVideoSelected}
                onVideoWasUploaded={onVideoWasUploaded}
                onVideoDragEnd={onMediaDragEnd}
            />
            <PackMediaSection
                type={MediaType.THUMBNAIL}
                header={'Thumbnail square (612×612 |  526x700 | 526x794)'}
                items={thumbnails}
                onMediaDelete={onMediaDelete}
                onMediasSelected={onPhotosSelected}
                onMediasWasUploaded={onPhotosWasUploaded}
                onMediaDragEnd={onMediaDragEnd}
                {...imageProcessingHandlers(MediaType.THUMBNAIL)}
            />
            {pack.type === PackType.MULTIPACK ? (
                <PackMediaSection
                    type={MediaType.WIDE_THUMBNAIL}
                    header={
                        'Thumbnail for bundle ( 930×523 | 994x666 | 912x620 | 828x574 )'
                    }
                    items={widethumbnails}
                    onMediaDelete={onMediaDelete}
                    onMediasSelected={onPhotosSelected}
                    onMediasWasUploaded={onPhotosWasUploaded}
                    onMediaDragEnd={onMediaDragEnd}
                    {...imageProcessingHandlers(MediaType.WIDE_THUMBNAIL)}
                />
            ) : null}
            <PackMediaSection
                type={MediaType.TAB_THUMBNAIL}
                header={'Tab Thumbnail (564 x 564)'}
                items={tabthumbnails}
                onMediaDelete={onMediaDelete}
                onMediasSelected={onPhotosSelected}
                onMediasWasUploaded={onPhotosWasUploaded}
                onMediaDragEnd={onMediaDragEnd}
                {...imageProcessingHandlers(MediaType.TAB_THUMBNAIL)}
            />
            <PackMediaSection
                type={MediaType.IAP_IMAGE}
                header={'In-App Purchase Image (1024 x 1024)'}
                items={iapimages}
                onMediaDelete={onMediaDelete}
                onMediasSelected={onPhotosSelected}
                onMediasWasUploaded={onPhotosWasUploaded}
                onMediaDragEnd={onMediaDragEnd}
                {...imageProcessingHandlers(MediaType.IAP_IMAGE)}
            />
        </>
    );
};
